import React, { useState, useEffect } from 'react';
import { formatDate, formatDateForInput, todayStr, firstDayOfMonthStr } from '../utils/dateUtils';
import MaskedDatePicker from './MaskedDatePicker';
import {
  TrendingUp,
  Package,
  User,
  ShoppingCart,
  Calendar,
  Search,
  ChevronUp,
  ChevronDown,
  Download,
  PieChart as PieChartIcon
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend
} from 'chart.js';
import { Pie } from 'react-chartjs-2';

ChartJS.register(ArcElement, Tooltip, Legend);

const CATEGORY_ORDER = ['SAT', 'Solar', 'MTF', 'MTB', 'Other'];

const CATEGORY_CONFIG = {
  SAT: {
    label: 'สินค้าดาวเทียม',
    gradient: 'from-blue-50 to-blue-100',
    border: 'border-blue-200',
    headerText: 'text-blue-800',
    badge: 'bg-blue-200 text-blue-800',
    dot: 'bg-blue-500',
    rank: 'bg-blue-500',
    pieColor: '#3b82f6',
    statsText: 'text-blue-600',
    statsBg: 'bg-blue-50 border-blue-200',
  },
  Solar: {
    label: 'สินค้าโซลาร์',
    gradient: 'from-amber-50 to-amber-100',
    border: 'border-amber-200',
    headerText: 'text-amber-800',
    badge: 'bg-amber-200 text-amber-800',
    dot: 'bg-amber-500',
    rank: 'bg-amber-500',
    pieColor: '#f59e0b',
    statsText: 'text-amber-600',
    statsBg: 'bg-amber-50 border-amber-200',
  },
  MTF: {
    label: 'Mounting (โรงงาน)',
    gradient: 'from-green-50 to-green-100',
    border: 'border-green-200',
    headerText: 'text-green-800',
    badge: 'bg-green-200 text-green-800',
    dot: 'bg-green-500',
    rank: 'bg-green-500',
    pieColor: '#10b981',
    statsText: 'text-green-600',
    statsBg: 'bg-green-50 border-green-200',
  },
  MTB: {
    label: 'Mounting (นำเข้า)',
    gradient: 'from-teal-50 to-teal-100',
    border: 'border-teal-200',
    headerText: 'text-teal-800',
    badge: 'bg-teal-200 text-teal-800',
    dot: 'bg-teal-500',
    rank: 'bg-teal-500',
    pieColor: '#06b6d4',
    statsText: 'text-teal-600',
    statsBg: 'bg-teal-50 border-teal-200',
  },
  Other: {
    label: 'สินค้าอื่น ๆ',
    gradient: 'from-gray-50 to-gray-100',
    border: 'border-gray-200',
    headerText: 'text-gray-700',
    badge: 'bg-gray-200 text-gray-700',
    dot: 'bg-gray-500',
    rank: 'bg-gray-500',
    pieColor: '#6b7280',
    statsText: 'text-gray-600',
    statsBg: 'bg-gray-50 border-gray-200',
  },
};

const TopProductsComponent = () => {
  const [productsData, setProductsData] = useState([]);
  const [groupedData, setGroupedData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [filters, setFilters] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'net_amount', direction: 'desc' });
  const [stockData, setStockData] = useState({});
  const [factoryTypes, setFactoryTypes] = useState({});
  const [categorizedProducts, setCategorizedProducts] = useState({});
  const [allCategorizedProducts, setAllCategorizedProducts] = useState({});

  useEffect(() => {
    setDateFrom(firstDayOfMonthStr());
    setDateTo(todayStr());
  }, []);

  // Get factory type for a product
  const getFactoryType = async (productCode) => {
    try {
      const response = await fetch('https://apisaleeasy.psisat.com/api/factory/type', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          "num_name_products": productCode.trim()
        })
      });

      if (!response.ok) {
        return { factory_type: 'Other' };
      }

      const data = await response.json();
      
      if (data.status && data.data) {
        return {
          factory_type: data.data.factory_type || 'Other',
          factory_name_products: data.data.factory_name_products || '',
        };
      } else {
        return { factory_type: 'Other' };
      }
    } catch (error) {
      console.error(`Error fetching factory type for ${productCode}:`, error);
      return { factory_type: 'Other' };
    }
  };

  // Get stock quantity for a product
  const getStockQuantity = async (productCode) => {
    try {
      const response = await fetch(`https://apisaleeasy.psisat.com/api/itemloc/total-qty/${productCode.trim()}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        return { total_ana_qty: 0 };
      }

      const data = await response.json();
      
      if (data.success && data.data) {
        return {
          total_ana_qty: data.data.total_ana_qty || 0,
        };
      } else {
        return { total_ana_qty: 0 };
      }
    } catch (error) {
      console.error(`Error fetching stock quantity for ${productCode}:`, error);
      return { total_ana_qty: 0 };
    }
  };

  // Get factory types for multiple products
  const getFactoryTypesForProducts = async (products) => {
    if (!products || products.length === 0) return {};

    const factoryTypePromises = products.map(async (product) => {
      const factoryInfo = await getFactoryType(product.product_code);
      return {
        product_code: product.product_code.trim(),
        factory_type: factoryInfo.factory_type,
        factory_name_products: factoryInfo.factory_name_products
      };
    });

    const results = await Promise.all(factoryTypePromises);
    
    const factoryTypeMap = {};
    results.forEach(result => {
      factoryTypeMap[result.product_code] = {
        factory_type: result.factory_type,
        factory_name_products: result.factory_name_products
      };
    });
    
    return factoryTypeMap;
  };

  // Get stock quantities for multiple products
  const getStockQuantitiesForProducts = async (products) => {
    if (!products || products.length === 0) return {};

    const stockPromises = products.map(async (product) => {
      const stockInfo = await getStockQuantity(product.product_code);
      return {
        product_code: product.product_code.trim(),
        total_ana_qty: stockInfo.total_ana_qty
      };
    });

    const results = await Promise.all(stockPromises);
    
    const stockMap = {};
    results.forEach(result => {
      stockMap[result.product_code] = result.total_ana_qty;
    });
    
    return stockMap;
  };

  const fetchStockQuantity = async (productCode) => {
    try {
      const response = await fetch(
        `https://apisaleeasy.psisat.com/api/itemloc/total-qty/${encodeURIComponent(productCode.trim())}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );

      if (!response.ok) {
        return null;
      }

      const result = await response.json();
      
      console.log(`Stock API response for ${productCode}:`, result);
      
      if (result.success && result.data) {
        return result.data.total_ana_qty || 0;
      }
      
      return null;
    } catch (err) {
      console.error(`Error fetching stock for ${productCode}:`, err);
      return null;
    }
  };

  const fetchAllStockQuantities = async (products) => {
    const stockPromises = products.map(async (product) => {
      const stock = await fetchStockQuantity(product.product_code);
      return {
        product_code: product.product_code.trim(),
        stock: stock
      };
    });

    const stockResults = await Promise.all(stockPromises);
    
    const stockMap = {};
    stockResults.forEach(result => {
      if (result.stock !== null) {
        stockMap[result.product_code] = result.stock;
      }
    });
    
    console.log('Final stock map:', stockMap);
    setStockData(stockMap);
  };

  const fetchTopProducts = async () => {
    if (!dateFrom || !dateTo) {
      setError('กรุณาเลือกวันที่เริ่มต้นและวันที่สิ้นสุด');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(
        `https://apisaleeasy.psisat.com/api/analytics/product-sales/top-products?dateFrom=${dateFrom}&dateTo=${dateTo}&limit=200`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        const rawData = result.data || [];
        setProductsData(rawData);
        
        // Group products by code and trim whitespace
        const grouped = groupProductsByCode(rawData);
        setGroupedData(grouped);
        
        // Fetch factory types for the products
        const factoryTypesResult = await getFactoryTypesForProducts(grouped);
        setFactoryTypes(factoryTypesResult);
        
        // Categorize products by factory type
        const categorized = categorizeProducts(grouped, factoryTypesResult);
        setAllCategorizedProducts(categorized);
        setCategorizedProducts(categorized);
        
        // Fetch stock quantities for all products
        const stockQuantitiesResult = await getStockQuantitiesForProducts(grouped);
        setStockData(stockQuantitiesResult);
        
        setFilters(result.filters);
      } else {
        throw new Error(result.message || 'เกิดข้อผิดพลาดในการดึงข้อมูล');
      }
    } catch (err) {
      console.error('Error fetching top products:', err);
      setError('เกิดข้อผิดพลาดในการดึงข้อมูล: ' + err.message);
      setProductsData([]);
      setGroupedData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (dateFrom && dateTo) {
      fetchTopProducts();
    }
  }, [dateFrom, dateTo]);

  // Filter categorized products based on search term
  useEffect(() => {
    if (!searchTerm) {
      setCategorizedProducts(allCategorizedProducts);
      return;
    }
    
    const filtered = {};
    Object.entries(allCategorizedProducts).forEach(([factoryType, products]) => {
      const filteredProducts = products.filter(product => {
        const searchLower = searchTerm.toLowerCase();
        const productCode = (product.product_code || '').toLowerCase();
        const productDescription = (product.product_description || '').toLowerCase();
        
        return productCode.includes(searchLower) || productDescription.includes(searchLower);
      });
      
      if (filteredProducts.length > 0) {
        filtered[factoryType] = filteredProducts;
      }
    });
    
    setCategorizedProducts(filtered);
  }, [searchTerm, allCategorizedProducts]);


  const formatNumber = (num) => {
    return new Intl.NumberFormat('th-TH').format(num);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('th-TH', {
      minimumFractionDigits: 2
    }).format(amount);
  };

  const categorizeProducts = (products, factoryTypesResult) => {
    const categorized = {};
    
    products.forEach(product => {
      const productCode = product.product_code.trim();
      let factoryType = factoryTypesResult[productCode]?.factory_type || 'Other';
      
      // Map to 5 main categories
      let normalizedType;
      const lowerFactoryType = factoryType.toLowerCase();
      if (lowerFactoryType === 'satellite' || lowerFactoryType === 'sat') {
        normalizedType = 'SAT';
      } else if (lowerFactoryType === 'solar') {
        normalizedType = 'Solar';
      } else if (lowerFactoryType === 'mtf') {
        normalizedType = 'MTF';
      } else if (lowerFactoryType === 'mtb') {
        normalizedType = 'MTB';
      } else if (lowerFactoryType === 'mounting') {
        normalizedType = 'MTF'; // map Mounting → MTF until DB is split
      } else {
        normalizedType = 'Other';
      }
      
      if (!categorized[normalizedType]) {
        categorized[normalizedType] = [];
      }
      categorized[normalizedType].push({
        ...product,
        factory_type: normalizedType,
        factory_name_products: factoryTypesResult[productCode]?.factory_name_products || ''
      });
    });
    
    return categorized;
  };

  const groupProductsByCode = (data) => {
    const grouped = {};
    
    data.forEach(item => {
      const productCode = (item.product_code || '').trim();
      
      // Filter out X0100001
      if (productCode === 'X0100001') return;
      
      if (!grouped[productCode]) {
        grouped[productCode] = {
          product_code: productCode,
          product_description: item.product_description || '',
          customer_count: 0,
          total_quantity: 0,
          net_amount: 0,
          invoice_count: 0,
          average_unit_price: 0,
          total_amount: 0
        };
      }
      
      grouped[productCode].customer_count += parseInt(item.customer_count) || 0;
      grouped[productCode].total_quantity += parseInt(item.total_quantity) || 0;
      grouped[productCode].net_amount += parseFloat(item.net_amount) || 0;
      grouped[productCode].invoice_count += parseInt(item.invoice_count) || 0;
      grouped[productCode].total_amount += parseFloat(item.net_amount) || 0;
    });
    
    // Calculate average unit price for each group and convert to array
    return Object.values(grouped).map(item => ({
      ...item,
      average_unit_price: item.total_quantity > 0 ? item.net_amount / item.total_quantity : 0
    })).sort((a, b) => b.net_amount - a.net_amount);
  };

  const handleSort = (key) => {
    let direction = 'desc';
    if (sortConfig.key === key && sortConfig.direction === 'desc') {
      direction = 'asc';
    }
    setSortConfig({ key, direction });
  };

  const sortData = (data) => {
    if (!sortConfig.key) return data;

    return [...data].sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];

      if (sortConfig.direction === 'asc') {
        if (typeof aVal === 'string') {
          return aVal.localeCompare(bVal);
        }
        return aVal - bVal;
      } else {
        if (typeof aVal === 'string') {
          return bVal.localeCompare(aVal);
        }
        return bVal - aVal;
      }
    });
  };

  // We now use categorizedProducts directly, so this is simplified
  const sortedData = groupedData;

  const handleExportCategoryExcel = (factoryType, products) => {
    if (!products || products.length === 0) {
      alert('ไม่มีข้อมูลสำหรับ export');
      return;
    }

    const categoryNames = Object.fromEntries(
      Object.entries(CATEGORY_CONFIG).map(([k, v]) => [k, v.label])
    );

    // Prepare data for Excel
    const excelData = products.map((product, index) => ({
      'ลำดับ': index + 1,
      'รหัสสินค้า': product.product_code,
      'รายละเอียดสินค้า': product.product_description,
      'หมวดหมู่': categoryNames[factoryType] || factoryType,
      'จำนวนที่ขายได้': product.total_quantity,
      'ยอดขาย (บาท)': product.net_amount,
      'ราคาเฉลี่ยต่อหน่วย': product.average_unit_price,
      'คงเหลือ': stockData[product.product_code.trim()] !== undefined ? stockData[product.product_code.trim()] : '-'
    }));

    // Add header information
    const headerInfo = [
      [`รายงานสินค้าขายดี - ${categoryNames[factoryType] || factoryType}`],
      [`ข้อมูลตั้งแต่: ${dateFrom} ถึง ${dateTo}`],
      [`จำนวนสินค้าในหมวดหมู่: ${products.length} รายการ`],
      [`วันที่ export: ${formatDate(new Date())}`],
      [''],
      ['ลำดับ', 'รหัสสินค้า', 'รายละเอียดสินค้า', 'หมวดหมู่', 'จำนวนที่ขายได้', 'ยอดขาย (บาท)', 'ราคาเฉลี่ยต่อหน่วย', 'คงเหลือ']
    ];

    // Combine header and data
    const finalData = [
      ...headerInfo,
      ...excelData.map(row => [
        row['ลำดับ'], 
        row['รหัสสินค้า'], 
        row['รายละเอียดสินค้า'], 
        row['หมวดหมู่'], 
        row['จำนวนที่ขายได้'], 
        row['ยอดขาย (บาท)'],
        row['ราคาเฉลี่ยต่อหน่วย'],
        row['คงเหลือ']
      ])
    ];

    // Add summary row
    const totalQuantity = products.reduce((sum, product) => sum + product.total_quantity, 0);
    const totalAmount = products.reduce((sum, product) => sum + product.net_amount, 0);
    
    finalData.push(['']);
    finalData.push([
      'สรุปรวม',
      '',
      '',
      '',
      totalQuantity,
      totalAmount,
      '',
      ''
    ]);

    // Create worksheet
    const ws = XLSX.utils.aoa_to_sheet(finalData);
    
    // Set column widths
    ws['!cols'] = [
      { width: 8 },  // ลำดับ
      { width: 15 }, // รหัสสินค้า
      { width: 40 }, // รายละเอียดสินค้า
      { width: 15 }, // หมวดหมู่
      { width: 15 }, // จำนวนที่ขายได้
      { width: 15 }, // ยอดขาย
      { width: 15 }, // ราคาเฉลี่ย
      { width: 15 }  // คงเหลือ
    ];

    // Create workbook
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, categoryNames[factoryType] || factoryType);

    // Generate Excel file
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    
    // Save file
    const fileName = `${categoryNames[factoryType] || factoryType}_${dateFrom}_${dateTo}.xlsx`;
    saveAs(blob, fileName);
  };

  // Generate Pie Chart data
  const getPieChartData = () => {
    const labels = [];
    const data = [];
    const backgroundColor = [];
    const borderColor = [];

    CATEGORY_ORDER.forEach((categoryKey) => {
      const products = categorizedProducts[categoryKey];
      if (!products) return;
      const totalAmount = products.reduce((sum, product) => sum + product.net_amount, 0);
      if (totalAmount > 0) {
        const cfg = CATEGORY_CONFIG[categoryKey] || CATEGORY_CONFIG.Other;
        labels.push(cfg.label);
        data.push(totalAmount);
        backgroundColor.push(cfg.pieColor);
        borderColor.push('#ffffff');
      }
    });

    return {
      labels,
      datasets: [{
        data,
        backgroundColor,
        borderColor,
        borderWidth: 2,
        hoverBorderWidth: 3,
        hoverOffset: 10
      }]
    };
  };

  const pieChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          padding: 15,
          usePointStyle: true,
          font: {
            size: 12
          }
        }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const value = context.parsed;
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = ((value / total) * 100).toFixed(1);
            return `${context.label}: ${value.toLocaleString('th-TH', { minimumFractionDigits: 0 })} บาท (${percentage}%)`;
          }
        }
      }
    }
  };

  const handleExportExcel = () => {
    if (sortedData.length === 0) {
      alert('ไม่มีข้อมูลสำหรับ export');
      return;
    }

    // Prepare data for Excel - Sheet 1 (All Products)
    const excelData = sortedData.map((product, index) => ({
      'ลำดับ': index + 1,
      'รหัสสินค้า': product.product_code,
      'รายละเอียดสินค้า': product.product_description,
      'หมวดหมู่': product.category,
      'จำนวนที่ขายได้': product.total_quantity,
      'คงเหลือ': stockData[product.product_code.trim()] !== undefined ? stockData[product.product_code.trim()] : '-'
    }));

    // Add header information for Sheet 1
    const headerInfo = [
      ['รายงานสินค้าขายดี'],
      [`ข้อมูลตั้งแต่: ${dateFrom} ถึง ${dateTo}`],
      [`หมวดหมู่: ทั้งหมด`],
      [`วันที่ export: ${formatDate(new Date())}`],
      [''], // Empty row
      ['ลำดับ', 'รหัสสินค้า', 'รายละเอียดสินค้า', 'หมวดหมู่', 'จำนวนที่ขายได้', 'คงเหลือ']
    ];

    // Combine header and data for Sheet 1
    const finalData = [
      ...headerInfo,
      ...excelData.map(row => [row['ลำดับ'], row['รหัสสินค้า'], row['รายละเอียดสินค้า'], row['หมวดหมู่'], row['จำนวนที่ขายได้'], row['คงเหลือ']])
    ];

    // Create Sheet 1
    const ws1 = XLSX.utils.aoa_to_sheet(finalData);
    
    // Set column widths for Sheet 1
    ws1['!cols'] = [
      { width: 8 },  // ลำดับ
      { width: 15 }, // รหัสสินค้า
      { width: 40 }, // รายละเอียดสินค้า
      { width: 15 }, // หมวดหมู่
      { width: 15 }, // จำนวนที่ขายได้
      { width: 20 }  // คงเหลือ
    ];

    // Prepare data for Sheet 2 (Categorized Products)
    const categorizedData = [];
    
    // Add header information for Sheet 2
    categorizedData.push(
      ['รายงานสินค้าขายดี - แยกตามประเภท'],
      [`ข้อมูลตั้งแต่: ${dateFrom} ถึง ${dateTo}`],
      [`วันที่ export: ${formatDate(new Date())}`],
      [''] // Empty row
    );

    // Add categorized products
    const categoryNames = Object.fromEntries(
      Object.entries(CATEGORY_CONFIG).map(([k, v]) => [k, v.label])
    );

    CATEGORY_ORDER.forEach((category) => {
      if (categorizedProducts[category] && categorizedProducts[category].length > 0) {
        const products = categorizedProducts[category];
        
        // Add category header
        categorizedData.push(['']);
        categorizedData.push([`${categoryNames[category]} (${products.length} รายการ)`]);
        categorizedData.push(['ลำดับ', 'รหัสสินค้า', 'รายละเอียดสินค้า', 'หมวดหมู่', 'จำนวนที่ขายได้', 'คงเหลือ']);
        
        // Add products in this category
        products.forEach((product, index) => {
          categorizedData.push([
            index + 1,
            product.product_code,
            product.product_description,
            categoryNames[category],
            product.total_quantity,
            stockData[product.product_code.trim()] !== undefined ? stockData[product.product_code.trim()] : '-'
          ]);
        });
        
        // Add category summary
        const totalQuantity = products.reduce((sum, product) => sum + product.total_quantity, 0);
        const totalAmount = products.reduce((sum, product) => sum + product.net_amount, 0);
        categorizedData.push(['']);
        categorizedData.push([
          'รวม',
          '',
          '',
          '',
          totalQuantity,
          ''
        ]);
        categorizedData.push([
          'ยอดขายรวม (บาท)',
          '',
          '',
          '',
          totalAmount.toLocaleString('th-TH', { minimumFractionDigits: 2 }),
          ''
        ]);
      }
    });

    // Create Sheet 2
    const ws2 = XLSX.utils.aoa_to_sheet(categorizedData);
    
    // Set column widths for Sheet 2
    ws2['!cols'] = [
      { width: 8 },  // ลำดับ
      { width: 15 }, // รหัสสินค้า
      { width: 40 }, // รายละเอียดสินค้า
      { width: 15 }, // หมวดหมู่
      { width: 15 }, // จำนวนที่ขายได้
      { width: 20 }  // คงเหลือ
    ];

    // Create workbook and add both sheets
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws1, 'สินค้าขายดี');
    XLSX.utils.book_append_sheet(wb, ws2, 'แยกตามประเภท');

    // Generate Excel file
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    
    // Save file
    const fileName = `สินค้าขายดี_${dateFrom}_${dateTo}.xlsx`;
    saveAs(blob, fileName);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <TrendingUp className="w-6 h-6 text-green-600" />
                  สินค้าขายดี
                </h1>
                <p className="text-gray-600">รายงานสินค้าที่มียอดขายสูงสุด</p>
              </div>
            </div>
            <div>
              <button
                onClick={handleExportExcel}
                disabled={loading || sortedData.length === 0}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Export Excel
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4 items-end">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Calendar className="w-4 h-4 inline mr-1" />
                วันที่เริ่มต้น
              </label>
              <MaskedDatePicker
                value={dateFrom}
                onChange={setDateFrom}
                placeholderText="DD/MM/YYYY"
                minDate={new Date('2025-11-01T00:00:00')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Calendar className="w-4 h-4 inline mr-1" />
                วันที่สิ้นสุด
              </label>
              <MaskedDatePicker
                value={dateTo}
                onChange={setDateTo}
                placeholderText="DD/MM/YYYY"
                maxDate={new Date()}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>
            <button
              onClick={fetchTopProducts}
              disabled={loading || !dateFrom || !dateTo}
              className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  กำลังโหลด...
                </>
              ) : (
                <>
                  <TrendingUp className="w-4 h-4" />
                  ค้นหา
                </>
              )}
            </button>
          </div>
        </div>


        {/* Filters Info */}
        {filters && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <h3 className="font-medium text-blue-900 mb-2">ข้อมูลที่แสดง:</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm text-blue-700">
              <div>
                <span className="font-medium">ระยะเวลา:</span> {filters.dateFrom} ถึง {filters.dateTo}
              </div>
              <div>
                <span className="font-medium">รหัสบริษัท:</span> {filters.companyCode}
              </div>
              <div>
                <span className="font-medium">จำนวนสินค้า:</span> {sortedData.length} รายการ
              </div>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {/* Pie Chart Card */}
        {groupedData.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                <PieChartIcon className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900">สัดส่วนยอดขายตามประเภทสินค้า</h3>
                <p className="text-gray-600">แสดงสัดส่วนยอดขายของแต่ละหมวดหมู่สินค้า</p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Pie Chart */}
              <div>
                <div className="h-80 flex items-center justify-center">
                  <Pie data={getPieChartData()} options={pieChartOptions} />
                </div>
              </div>
              
              {/* Statistics */}
              <div className="space-y-3">
                <h4 className="font-semibold text-gray-900">สรุปยอดขายรวม</h4>
                {CATEGORY_ORDER.map((categoryKey) => {
                  const products = categorizedProducts[categoryKey] || [];
                  const cfg = CATEGORY_CONFIG[categoryKey] || CATEGORY_CONFIG.Other;
                  const totalAmount = products.reduce((sum, p) => sum + p.net_amount, 0);
                  const totalQuantity = products.reduce((sum, p) => sum + p.total_quantity, 0);
                  const grandTotal = CATEGORY_ORDER
                    .filter(k => categorizedProducts[k])
                    .reduce((sum, k) => sum + categorizedProducts[k].reduce((s, p) => s + p.net_amount, 0), 0);
                  const pct = grandTotal > 0 ? ((totalAmount / grandTotal) * 100).toFixed(1) : '0.0';

                  return (
                    <div key={categoryKey} className={`rounded-lg p-3 border ${cfg.statsBg}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div className={`w-3 h-3 rounded-full ${cfg.dot}`}></div>
                          <span className={`text-sm font-semibold ${cfg.headerText}`}>{cfg.label}</span>
                        </div>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${cfg.badge}`}>
                          {products.length} รายการ
                        </span>
                      </div>
                      <div className="grid grid-cols-3 gap-1 text-xs">
                        <div>
                          <p className="text-gray-500">ยอดขาย</p>
                          <p className={`font-bold ${cfg.statsText}`}>
                            {totalAmount >= 1_000_000
                              ? `${(totalAmount / 1_000_000).toFixed(1)}M`
                              : totalAmount.toLocaleString('th-TH', { maximumFractionDigits: 0 })}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-500">จำนวน</p>
                          <p className="font-bold text-gray-700">{totalQuantity.toLocaleString()} ชิ้น</p>
                        </div>
                        <div>
                          <p className="text-gray-500">สัดส่วน</p>
                          <p className={`font-bold ${cfg.statsText}`}>{pct}%</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Categorized Products Tables */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {groupedData.length > 0 ? (
            CATEGORY_ORDER.map((factoryType) => {
              const products = categorizedProducts[factoryType] || [];
              const cfg = CATEGORY_CONFIG[factoryType] || CATEGORY_CONFIG.Other;
              return (
              <div key={factoryType} className={`bg-white rounded-xl shadow-sm border-2 ${cfg.border} overflow-hidden`}>
                {/* Category Header */}
                <div className={`bg-gradient-to-r ${cfg.gradient} px-4 py-3 border-b ${cfg.border}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className={`w-3 h-3 rounded-full ${cfg.dot}`}></div>
                      <h4 className={`font-bold text-sm ${cfg.headerText}`}>
                        {cfg.label}
                        <span className="ml-1 text-xs font-normal opacity-70">({factoryType})</span>
                      </h4>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${cfg.badge}`}>
                        {products.length} รายการ
                      </span>
                    </div>
                    <button
                      onClick={() => handleExportCategoryExcel(factoryType, products)}
                      className="px-2 py-1 bg-white/70 border border-current rounded text-xs hover:bg-white transition-colors flex items-center gap-1 font-medium"
                      style={{ color: cfg.pieColor }}
                      title={`Export ${factoryType} Excel`}
                    >
                      <Download className="w-3 h-3" />
                      Export
                    </button>
                  </div>
                  {/* Total Summary */}
                  <div className="flex gap-4 text-xs">
                    <div>
                      <span className="text-gray-500">จำนวนรวม </span>
                      <span className={`font-bold ${cfg.headerText}`}>
                        {products.reduce((sum, p) => sum + p.total_quantity, 0).toLocaleString()} ชิ้น
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500">ยอดขายรวม </span>
                      <span className="font-bold text-green-700">
                        {products.reduce((sum, p) => sum + p.net_amount, 0).toLocaleString('th-TH', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} ฿
                      </span>
                    </div>
                  </div>
                </div>

                {/* Products Table */}
                <div className="overflow-x-auto h-96 overflow-y-auto">
                  <table className="min-w-full divide-y divide-gray-100">
                    <thead className="sticky top-0 bg-white/95 backdrop-blur-sm shadow-sm">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/2">
                          สินค้า
                        </th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-1/2">
                          ยอดขาย / คงเหลือ
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {products.length === 0 && (
                        <tr>
                          <td colSpan={2} className="px-3 py-8 text-center text-xs text-gray-400">
                            ไม่มียอดขายในช่วงเวลานี้
                          </td>
                        </tr>
                      )}
                      {products.map((product, index) => (
                        <tr key={`${factoryType}-${index}`} className="hover:bg-gray-50 transition-colors">
                          <td className="px-3 py-2 w-1/2">
                            <div className="flex items-start gap-2 max-w-full">
                              <span className={`inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white ${cfg.rank} rounded-full flex-shrink-0`}>
                                {index + 1}
                              </span>
                              <div className="min-w-0 flex-1 overflow-hidden w-32">
                                <p className="text-xs font-semibold text-gray-800 truncate w-full" title={product.product_code}>
                                  {product.product_code}
                                </p>
                                <p className="text-xs text-gray-500 truncate w-full" title={product.product_description}>
                                  {product.product_description}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td className="px-3 py-2 w-1/2 text-right">
                            <div className="min-w-0">
                              <p className="text-sm font-bold text-green-600 truncate">
                                {product.net_amount.toLocaleString('th-TH', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} ฿
                              </p>
                              <p className="text-xs text-gray-400 truncate">
                                ขาย {product.total_quantity.toLocaleString()} ชิ้น
                              </p>
                              <p className={`text-xs truncate ${cfg.statsText}`}>
                                คงเหลือ {stockData[product.product_code.trim()] !== undefined
                                  ? stockData[product.product_code.trim()].toLocaleString()
                                  : '—'} ชิ้น
                              </p>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              );
            })
          ) : loading ? (
            <div className="col-span-full bg-white rounded-lg shadow-sm border border-gray-200 p-12">
              <div className="flex items-center justify-center">
                <div className="w-8 h-8 border-4 border-green-600 border-t-transparent rounded-full animate-spin mr-3"></div>
                <span className="text-gray-500">กำลังโหลดข้อมูล...</span>
              </div>
            </div>
          ) : (
            <div className="col-span-full bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
              <div className="text-gray-500">
                <Package className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                <h3 className="text-lg font-medium mb-2">ไม่มีข้อมูลสินค้า</h3>
                <p>กรุณาเลือกช่วงวันที่และกดปุ่ม "ค้นหา"</p>
              </div>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default TopProductsComponent;